import { useEffect, useState } from "react";
import { api, formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { Rss, Play, Loader2, RefreshCw, CopyX, X, Link2, PenLine, Sparkles, Ban, Lock } from "lucide-react";

// Clone de la Revue de presse SANS la partie « articles » (liste, performance, modération).
// Scaffold prêt à recevoir des instructions spécifiques pour « Revue de presse 3 art ».
export default function AdminPressReview3Art() {
  const [cfg, setCfg] = useState(null);
  const [loading, setLoading] = useState(true);
  const [running, setRunning] = useState(false);
  const [deduping, setDeduping] = useState(false);
  const [saving, setSaving] = useState(false);
  const [genMode, setGenMode] = useState("url");
  const [genUrl, setGenUrl] = useState("");
  const [genDesc, setGenDesc] = useState("");
  const [generating, setGenerating] = useState(false);
  const [genInstr, setGenInstr] = useState("");
  const [genRemember, setGenRemember] = useState(false);
  const [newBlocked, setNewBlocked] = useState("");
  const [newRecommended, setNewRecommended] = useState("");
  const [reillustrating, setReillustrating] = useState(false);
  const [reJob, setReJob] = useState(null);
  const [recStats, setRecStats] = useState({});
  const [preview, setPreview] = useState(null);
  const [previewLoading, setPreviewLoading] = useState(false);
  const [trioSector, setTrioSector] = useState("");
  const [trioTheme, setTrioTheme] = useState("");
  const [trioBusy, setTrioBusy] = useState(false);
  const [trioUrl, setTrioUrl] = useState("");

  const generateTrio = () => {
    const sector = trioSector.trim();
    if (sector.length < 2) { toast.error("Indiquez un secteur géographique (ex. Dreux)."); return; }
    setTrioBusy(true); setTrioUrl("");
    api.post("/admin/press-review/generate-trio", { sector, theme: trioTheme.trim() })
      .then((r) => {
        const jobId = r.data.job_id;
        toast.info("Génération lancée… (~1 min)");
        const poll = () => {
          api.get(`/admin/press-review/trio-status?job=${jobId}`)
            .then((s) => {
              if (s.data.status === "done") {
                setTrioUrl(s.data.url); setTrioBusy(false);
                toast.success(`Revue « 3 articles » créée : ${s.data.title}`);
              } else if (s.data.status === "error") {
                setTrioBusy(false); toast.error(s.data.error || "Génération échouée.");
              } else { setTimeout(poll, 4000); }
            })
            .catch(() => { setTrioBusy(false); toast.error("Suivi de la génération interrompu."); });
        };
        setTimeout(poll, 4000);
      })
      .catch((e) => { setTrioBusy(false); toast.error(formatApiError(e)); });
  };

  const loadPreview = () => {
    if (!cfg) return;
    setPreviewLoading(true);
    api.post("/admin/press-review/preview-effects", {
      illustrate_enabled: !!cfg.illustrate_enabled,
      illustrate_strength: cfg.illustrate_strength ?? 0.5,
      watercolor_enabled: !!cfg.watercolor_enabled,
      watercolor_strength: cfg.watercolor_strength ?? 0.5,
    }).then((r) => setPreview(r.data)).catch(() => {}).finally(() => setPreviewLoading(false));
  };

  const loadRecStats = () => {
    api.get("/admin/press-review/recommended-stats")
      .then((r) => setRecStats(r.data.counts || {}))
      .catch(() => {});
  };

  const loadAll = () => {
    setLoading(true);
    api.get("/admin/press-review/settings")
      .then((s) => setCfg(s.data))
      .catch((e) => toast.error(formatApiError(e)))
      .finally(() => setLoading(false));
    loadRecStats();
    api.get("/admin/press-review/reillustrate-status").then((r) => {
      setReJob(r.data);
      if (r.data.running) { setReillustrating(true); setTimeout(pollReillustrate, 4000); }
    }).catch(() => {});
  };
  useEffect(loadAll, []);

  useEffect(() => {
    if (!cfg) return;
    const t = setTimeout(loadPreview, 600);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [cfg?.illustrate_enabled, cfg?.illustrate_strength, cfg?.watercolor_enabled, cfg?.watercolor_strength]);

  const saveSettings = (patch) => {
    setSaving(true);
    api.post("/admin/press-review/settings", patch)
      .then((r) => { setCfg((c) => ({ ...c, ...r.data })); toast.success("Réglages enregistrés"); })
      .catch((e) => toast.error(formatApiError(e)))
      .finally(() => setSaving(false));
  };

  const runNow = () => {
    setRunning(true);
    api.post("/admin/press-review/run")
      .then(() => { toast.success("Collecte lancée."); setTimeout(loadAll, 45000); })
      .catch((e) => toast.error(formatApiError(e)))
      .finally(() => setTimeout(() => setRunning(false), 2000));
  };

  const dedupe = () => {
    if (!window.confirm("Rechercher et supprimer les doublons ?")) return;
    setDeduping(true);
    api.post("/admin/press-review/dedupe")
      .then((r) => { const n = r.data.removed || 0; toast.success(n ? `${n} doublon(s) supprimé(s).` : "Aucun doublon trouvé ✅"); })
      .catch((e) => toast.error(formatApiError(e)))
      .finally(() => setDeduping(false));
  };

  const generateManual = () => {
    const url = genUrl.trim();
    const description = genDesc.trim();
    if (genMode === "url" && !url) { toast.error("Collez le lien de l'article."); return; }
    if (genMode === "desc" && description.length < 15) { toast.error("Décrivez le sujet (15 caractères minimum)."); return; }
    setGenerating(true);
    const base = genMode === "url" ? { url } : { description };
    const payload = { ...base, instructions: genInstr.trim(), remember: genRemember };
    api.post("/admin/press-review/generate-manual", payload)
      .then((r) => {
        toast.success(`Article créé : « ${r.data.title} »`);
        setGenUrl(""); setGenDesc(""); setGenInstr(""); setGenRemember(false);
      })
      .catch((e) => toast.error(formatApiError(e)))
      .finally(() => setGenerating(false));
  };

  const saveStandingInstructions = () => {
    saveSettings({ standing_instructions: (cfg.standing_instructions || "").trim() });
    toast.success("Consignes permanentes enregistrées.");
  };

  const addBlocked = () => {
    const v = newBlocked.trim().toLowerCase();
    if (!v) return;
    const locked = (cfg.blocked_locked || []).map((x) => x.toLowerCase());
    const extras = cfg.blocked_sources || [];
    if (locked.includes(v) || extras.includes(v)) { toast.info("Cette source est déjà bloquée."); setNewBlocked(""); return; }
    saveSettings({ blocked_sources: [...extras, v] });
    setNewBlocked("");
  };

  const removeBlocked = (v) => {
    saveSettings({ blocked_sources: (cfg.blocked_sources || []).filter((x) => x !== v) });
  };

  const addRecommended = () => {
    const v = newRecommended.trim().toLowerCase();
    if (!v) return;
    const rec = cfg.recommended_sources || [];
    if (rec.includes(v)) { toast.info("Cette source est déjà recommandée."); setNewRecommended(""); return; }
    saveSettings({ recommended_sources: [...rec, v] });
    setNewRecommended("");
    setTimeout(loadRecStats, 500);
  };

  const removeRecommended = (v) => {
    saveSettings({ recommended_sources: (cfg.recommended_sources || []).filter((x) => x !== v) });
  };

  const pollReillustrate = () => {
    api.get("/admin/press-review/reillustrate-status").then((r) => {
      setReJob(r.data);
      if (r.data.running) { setTimeout(pollReillustrate, 4000); }
      else { setReillustrating(false); toast.success(`Illustrations régénérées (${r.data.ok || 0}/${r.data.total || 0}).`); }
    }).catch(() => setReillustrating(false));
  };

  const reillustrateAll = () => {
    if (!window.confirm("Régénérer les illustrations de TOUS les articles ? Consomme des crédits IA.")) return;
    setReillustrating(true);
    api.post("/admin/press-review/reillustrate-all").then((r) => {
      setReJob({ running: true, total: r.data.total || 0, done: 0, ok: 0 });
      toast.success("Régénération lancée.");
      setTimeout(pollReillustrate, 4000);
    }).catch((e) => { setReillustrating(false); toast.error(formatApiError(e)); });
  };

  if (loading || !cfg) return <div className="p-8 text-slate-500">Chargement…</div>;

  return (
    <div className="max-w-5xl mx-auto px-4 py-8" data-testid="admin-press-review-3art">
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div>
          <h1 className="font-heading text-2xl font-bold text-slate-900 flex items-center gap-2">
            <Rss size={22} className="text-[#E05638]" /> Revue de presse 3 art
          </h1>
          <p className="mt-1 text-sm text-slate-500">
            Clone de la revue de presse (sans la liste d'articles). En attente de configuration.
          </p>
        </div>
        <div className="flex gap-2">
          <button onClick={loadAll} data-testid="press3-refresh-btn"
            className="inline-flex items-center gap-1.5 px-3 py-2 rounded-lg border border-slate-200 text-sm text-slate-600 hover:bg-slate-50">
            <RefreshCw size={15} /> Actualiser
          </button>
          <button onClick={dedupe} disabled={deduping} data-testid="press3-dedupe-btn"
            className="inline-flex items-center gap-1.5 px-3 py-2 rounded-lg border border-slate-200 text-sm text-slate-600 hover:bg-slate-50 disabled:opacity-60">
            {deduping ? <Loader2 size={15} className="animate-spin" /> : <CopyX size={15} />} Supprimer les doublons
          </button>
          <button onClick={runNow} disabled={running} data-testid="press3-run-btn"
            className="inline-flex items-center gap-1.5 px-4 py-2 rounded-lg bg-[#E05638] text-white text-sm font-semibold hover:bg-[#c8482d] disabled:opacity-60">
            {running ? <Loader2 size={15} className="animate-spin" /> : <Play size={15} />} Générer maintenant
          </button>
        </div>
      </div>

      {/* Génération d'une revue « 3 articles » */}
      <div className="mt-6 p-5 rounded-2xl border border-[#E05638]/30 bg-gradient-to-br from-orange-50 to-white" data-testid="trio-generate-card">
        <div className="flex items-center gap-2">
          <Sparkles size={18} className="text-[#E05638]" />
          <div className="font-semibold text-slate-900">Créer une revue « 3 articles »</div>
        </div>
        <p className="mt-1 text-xs text-slate-500">
          Génère 3 nouveaux articles IA d'un même secteur géographique, regroupés en une page publique avec visuel triptyque.
        </p>
        <div className="mt-3 grid sm:grid-cols-2 gap-3">
          <div>
            <label className="text-xs font-semibold text-slate-500">Secteur géographique</label>
            <input value={trioSector} onChange={(e) => setTrioSector(e.target.value)} data-testid="trio-sector"
              placeholder="ex. Dreux, Chartres, Nogent-le-Rotrou…" className="mt-1 w-full px-3 py-2 rounded-lg border border-slate-200 text-sm" />
          </div>
          <div>
            <label className="text-xs font-semibold text-slate-500">Thème (facultatif)</label>
            <input value={trioTheme} onChange={(e) => setTrioTheme(e.target.value)} data-testid="trio-theme"
              placeholder="ex. culture, sport, commerces…" className="mt-1 w-full px-3 py-2 rounded-lg border border-slate-200 text-sm" />
          </div>
        </div>
        <button onClick={generateTrio} disabled={trioBusy} data-testid="trio-generate-btn"
          className="mt-3 inline-flex items-center gap-1.5 px-4 py-2 rounded-lg bg-[#E05638] text-white text-sm font-semibold hover:bg-[#c8482d] disabled:opacity-60">
          {trioBusy ? <Loader2 size={15} className="animate-spin" /> : <Sparkles size={15} />}
          {trioBusy ? "Génération en cours… (~1 min)" : "Générer la revue 3 articles"}
        </button>
        {trioUrl && (
          <a href={trioUrl} target="_blank" rel="noopener noreferrer" data-testid="trio-result-link"
            className="mt-3 block text-sm font-semibold text-[#E05638] hover:underline">
            ✅ Voir la page publique : {trioUrl}
          </a>
        )}
      </div>

      {/* Réglages */}
      <div className="mt-6 grid sm:grid-cols-2 gap-4">
        <div className="p-5 rounded-2xl border border-slate-200 bg-white">
          <div className="flex items-center justify-between">
            <div>
              <div className="font-semibold text-slate-900">Automatisation</div>
              <div className="text-xs text-slate-500">Publication automatique à 8h chaque matin</div>
            </div>
            <button onClick={() => saveSettings({ enabled: !cfg.enabled })} disabled={saving}
              data-testid="press3-toggle-enabled"
              className={`relative w-12 h-7 rounded-full transition-colors ${cfg.enabled ? "bg-emerald-500" : "bg-slate-300"}`}>
              <span className={`absolute top-1 left-1 w-5 h-5 rounded-full bg-white transition-transform ${cfg.enabled ? "translate-x-5" : ""}`} />
            </button>
          </div>
          <div className="mt-4">
            <label className="text-xs font-semibold text-slate-500 uppercase tracking-wide">Articles par jour</label>
            <div className="mt-1 flex items-center gap-2">
              <input type="number" min={1} max={15} defaultValue={cfg.per_day} data-testid="press3-per-day"
                onBlur={(e) => { const v = Math.max(1, Math.min(15, parseInt(e.target.value) || 5)); if (v !== cfg.per_day) saveSettings({ per_day: v }); }}
                className="w-20 px-3 py-2 rounded-lg border border-slate-200 text-sm" />
              <span className="text-xs text-slate-400">max 15</span>
            </div>
          </div>
        </div>

        <div className="p-5 rounded-2xl border border-slate-200 bg-white">
          <div className="font-semibold text-slate-900">Backlinks partenaires</div>
          <div className="mt-3 space-y-3">
            <div>
              <label className="text-xs font-semibold text-slate-500">Débouche Discount 28 (discret)</label>
              <input defaultValue={cfg.backlink_debouche_url} data-testid="press3-backlink-debouche"
                onBlur={(e) => { const v = e.target.value.trim(); if (v !== cfg.backlink_debouche_url) saveSettings({ backlink_debouche_url: v }); }}
                className="mt-1 w-full px-3 py-2 rounded-lg border border-slate-200 text-sm" />
            </div>
            <div>
              <label className="text-xs font-semibold text-slate-500">PROXIreduc (invitation)</label>
              <input defaultValue={cfg.backlink_proxireduc_url} data-testid="press3-backlink-proxireduc"
                onBlur={(e) => { const v = e.target.value.trim(); if (v !== cfg.backlink_proxireduc_url) saveSettings({ backlink_proxireduc_url: v }); }}
                className="mt-1 w-full px-3 py-2 rounded-lg border border-slate-200 text-sm" />
            </div>
          </div>
        </div>
      </div>

      {/* Création manuelle d'un article (lien ou description) */}
      <div className="mt-4 p-5 rounded-2xl border border-sky-200 bg-sky-50" data-testid="press3-manual-create-card">
        <div className="flex items-center gap-2">
          <Sparkles size={18} className="text-[#E05638]" />
          <div className="font-semibold text-slate-900">Créer un article à la demande</div>
        </div>
        <div className="mt-3 inline-flex rounded-xl bg-slate-100 p-1" data-testid="press3-manual-mode">
          <button onClick={() => setGenMode("url")} data-testid="press3-manual-mode-url"
            className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-sm font-semibold transition-colors ${genMode === "url" ? "bg-white text-[#E05638] shadow-sm" : "text-slate-500 hover:text-slate-700"}`}>
            <Link2 size={14} /> Depuis un lien
          </button>
          <button onClick={() => setGenMode("desc")} data-testid="press3-manual-mode-desc"
            className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-sm font-semibold transition-colors ${genMode === "desc" ? "bg-white text-[#E05638] shadow-sm" : "text-slate-500 hover:text-slate-700"}`}>
            <PenLine size={14} /> En décrivant le sujet
          </button>
        </div>

        {genMode === "url" ? (
          <div className="mt-3">
            <input value={genUrl} onChange={(e) => setGenUrl(e.target.value)}
              data-testid="press3-manual-url" placeholder="https://www.lechorepublicain.fr/..."
              className="w-full px-3 py-2 rounded-lg border border-slate-200 text-sm" />
          </div>
        ) : (
          <div className="mt-3">
            <textarea value={genDesc} onChange={(e) => setGenDesc(e.target.value)} rows={3}
              data-testid="press3-manual-desc" placeholder="Ex : Grève des agents territoriaux à Chartres cette semaine…"
              className="w-full px-3 py-2 rounded-lg border border-slate-200 text-sm resize-y" />
          </div>
        )}

        <div className="mt-3" data-testid="press3-manual-instructions-block">
          <label className="text-xs font-semibold text-slate-600">
            Consignes pour cet article <span className="font-normal text-slate-400">(facultatif)</span>
          </label>
          <textarea value={genInstr} onChange={(e) => setGenInstr(e.target.value)} rows={2}
            data-testid="press3-manual-instructions"
            className="mt-1 w-full px-3 py-2 rounded-lg border border-slate-200 text-sm resize-y" />
          <label className="mt-2 flex items-center gap-2 text-xs text-slate-600 cursor-pointer">
            <input type="checkbox" checked={genRemember} onChange={(e) => setGenRemember(e.target.checked)}
              data-testid="press3-manual-remember" className="accent-[#E05638]" />
            Mémoriser ces consignes pour <b>tous</b> les prochains articles
          </label>
        </div>

        <button onClick={generateManual} disabled={generating} data-testid="press3-manual-generate-btn"
          className="mt-3 inline-flex items-center gap-1.5 px-4 py-2 rounded-lg bg-[#E05638] text-white text-sm font-semibold hover:bg-[#c8482d] disabled:opacity-60">
          {generating ? <Loader2 size={15} className="animate-spin" /> : <Sparkles size={15} />}
          {generating ? "Génération en cours…" : "Générer l'article"}
        </button>
      </div>

      {/* Consignes permanentes */}
      <div className="mt-4 p-5 rounded-2xl border border-slate-200 bg-white" data-testid="press3-standing-card">
        <div className="flex items-center gap-2">
          <PenLine size={18} className="text-[#E05638]" />
          <div className="font-semibold text-slate-900">Consignes permanentes de la rédaction</div>
        </div>
        <textarea value={cfg.standing_instructions || ""}
          onChange={(e) => setCfg((c) => ({ ...c, standing_instructions: e.target.value }))}
          rows={4} data-testid="press3-standing-instructions"
          className="mt-2 w-full px-3 py-2 rounded-lg border border-slate-200 text-sm resize-y" />
        <button onClick={saveStandingInstructions} disabled={saving} data-testid="press3-standing-save-btn"
          className="mt-2 inline-flex items-center gap-1.5 px-4 py-2 rounded-lg bg-slate-900 text-white text-sm font-semibold hover:bg-slate-800 disabled:opacity-60">
          {saving ? <Loader2 size={15} className="animate-spin" /> : <PenLine size={15} />} Enregistrer les consignes
        </button>
      </div>

      {/* Sources interdites */}
      <div className="mt-4 p-5 rounded-2xl border border-slate-200 bg-white" data-testid="press3-blocked-card">
        <div className="flex items-center gap-2">
          <Ban size={18} className="text-red-600" />
          <div className="font-semibold text-slate-900">Sources interdites</div>
        </div>
        <div className="mt-3 flex flex-wrap gap-2" data-testid="press3-blocked-list">
          {(cfg.blocked_locked || []).map((b) => (
            <span key={`lock-${b}`} className="inline-flex items-center gap-1 rounded-full bg-slate-100 text-slate-600 text-xs px-2.5 py-1">
              <Lock size={11} className="text-slate-400" /> {b}
            </span>
          ))}
          {(cfg.blocked_sources || []).map((b) => (
            <span key={`extra-${b}`} className="inline-flex items-center gap-1 rounded-full bg-red-50 text-red-700 text-xs px-2.5 py-1">
              {b}
              <button onClick={() => removeBlocked(b)} disabled={saving} title="Retirer" className="ml-0.5 rounded-full hover:bg-red-100 p-0.5">
                <X size={11} />
              </button>
            </span>
          ))}
          {(cfg.blocked_sources || []).length === 0 && (cfg.blocked_locked || []).length === 0 && (
            <span className="text-xs text-slate-400">Aucune source interdite.</span>
          )}
        </div>
        <div className="mt-3 flex flex-wrap items-center gap-2">
          <input value={newBlocked} onChange={(e) => setNewBlocked(e.target.value)}
            onKeyDown={(e) => { if (e.key === "Enter") addBlocked(); }}
            data-testid="press3-blocked-input" placeholder="ex. lemonde.fr ou Le Parisien"
            className="flex-1 min-w-0 px-3 py-2 rounded-lg border border-slate-200 text-sm" />
          <button onClick={addBlocked} disabled={saving || !newBlocked.trim()} data-testid="press3-blocked-add-btn"
            className="inline-flex items-center gap-1.5 px-4 py-2 rounded-lg bg-red-600 text-white text-sm font-semibold hover:bg-red-700 disabled:opacity-60 shrink-0">
            <Ban size={15} /> Bloquer
          </button>
        </div>
      </div>

      {/* Sources recommandées */}
      <div className="mt-4 p-5 rounded-2xl border border-slate-200 bg-white" data-testid="press3-recommended-card">
        <div className="flex items-center gap-2">
          <Rss size={18} className="text-emerald-600" />
          <div className="font-semibold text-slate-900">Sources recommandées</div>
        </div>
        <div className="mt-3 flex flex-wrap gap-2" data-testid="press3-recommended-list">
          {(cfg.recommended_sources || []).map((b) => (
            <span key={`rec-${b}`} className="inline-flex items-center gap-1 rounded-full bg-emerald-50 text-emerald-700 text-xs px-2.5 py-1">
              {b}
              <span className="ml-0.5 inline-flex items-center rounded-full bg-emerald-600 text-white text-[10px] font-semibold px-1.5 py-0.5" title="Articles apportés ce mois-ci">
                {recStats[b] ?? 0} ce mois-ci
              </span>
              <button onClick={() => removeRecommended(b)} disabled={saving} title="Retirer" className="ml-0.5 rounded-full hover:bg-emerald-100 p-0.5">
                <X size={11} />
              </button>
            </span>
          ))}
          {(cfg.recommended_sources || []).length === 0 && (
            <span className="text-xs text-slate-400">Aucune source recommandée pour l'instant.</span>
          )}
        </div>
        <div className="mt-3 flex flex-wrap items-center gap-2">
          <input value={newRecommended} onChange={(e) => setNewRecommended(e.target.value)}
            onKeyDown={(e) => { if (e.key === "Enter") addRecommended(); }}
            data-testid="press3-recommended-input" placeholder="ex. ouest-france.fr ou actu.fr"
            className="flex-1 min-w-0 px-3 py-2 rounded-lg border border-slate-200 text-sm" />
          <button onClick={addRecommended} disabled={saving || !newRecommended.trim()} data-testid="press3-recommended-add-btn"
            className="inline-flex items-center gap-1.5 px-4 py-2 rounded-lg bg-emerald-600 text-white text-sm font-semibold hover:bg-emerald-700 disabled:opacity-60 shrink-0">
            <Rss size={15} /> Ajouter
          </button>
        </div>
      </div>

      {/* Signature illustrée */}
      <div className="mt-4 p-5 rounded-2xl border border-slate-200 bg-white" data-testid="press3-illustration-card">
        <div className="flex items-center justify-between gap-3">
          <div>
            <div className="font-semibold text-slate-900">Signature illustrée des images</div>
            <div className="text-xs text-slate-500">Transforme les images en illustrations éditoriales. Gratuit, composition préservée.</div>
          </div>
          <button onClick={() => saveSettings({ illustrate_enabled: !cfg.illustrate_enabled })} disabled={saving}
            data-testid="press3-toggle-illustrate"
            className={`relative w-12 h-7 rounded-full transition-colors shrink-0 ${cfg.illustrate_enabled ? "bg-emerald-500" : "bg-slate-300"}`}>
            <span className={`absolute top-1 left-1 w-5 h-5 rounded-full bg-white transition-transform ${cfg.illustrate_enabled ? "translate-x-5" : ""}`} />
          </button>
        </div>
        <div className="mt-4">
          <div className="flex items-center justify-between">
            <label className="text-xs font-semibold text-slate-500 uppercase tracking-wide">Intensité de l'effet</label>
            <span className="text-sm font-bold text-slate-900" data-testid="press3-illustrate-strength-value">
              {Math.round((cfg.illustrate_strength ?? 0.5) * 100)}%
            </span>
          </div>
          <input type="range" min={0.25} max={0.7} step={0.05} defaultValue={cfg.illustrate_strength ?? 0.5}
            data-testid="press3-illustrate-strength" disabled={!cfg.illustrate_enabled}
            onChange={(e) => setCfg((c) => ({ ...c, illustrate_strength: parseFloat(e.target.value) }))}
            onMouseUp={(e) => saveSettings({ illustrate_strength: parseFloat(e.target.value) })}
            onTouchEnd={(e) => saveSettings({ illustrate_strength: parseFloat(e.target.value) })}
            className="mt-2 w-full accent-[#E05638] disabled:opacity-50" />
        </div>

        <div className="mt-4 pt-4 border-t border-slate-100" data-testid="press3-preview-section">
          <div className="flex items-center justify-between">
            <div className="font-semibold text-slate-900 text-sm">Aperçu en direct</div>
            <button onClick={loadPreview} disabled={previewLoading} data-testid="press3-preview-refresh"
              className="text-xs text-slate-500 hover:text-slate-800 inline-flex items-center gap-1">
              <RefreshCw size={12} className={previewLoading ? "animate-spin" : ""} /> Actualiser
            </button>
          </div>
          <div className="mt-3 grid grid-cols-2 gap-3">
            <figure className="m-0">
              <img src={preview?.original} alt="Avant" data-testid="press3-preview-before"
                className="w-full h-32 sm:h-36 object-cover rounded-lg border border-slate-200 bg-slate-100" />
              <figcaption className="mt-1 text-[11px] text-slate-400 text-center">Avant (image normale)</figcaption>
            </figure>
            <figure className="m-0 relative">
              {previewLoading && (
                <div className="absolute inset-0 flex items-center justify-center bg-white/60 rounded-lg">
                  <Loader2 size={18} className="animate-spin text-slate-500" />
                </div>
              )}
              <img src={preview?.result || preview?.original} alt="Après" data-testid="press3-preview-after"
                className="w-full h-32 sm:h-36 object-cover rounded-lg border border-slate-200 bg-slate-100" />
              <figcaption className="mt-1 text-[11px] text-slate-400 text-center">Après</figcaption>
            </figure>
          </div>
        </div>

        <div className="mt-4 pt-4 border-t border-slate-100">
          <div className="font-semibold text-slate-900 text-sm">Régénérer les anciens articles</div>
          {reillustrating && reJob && (
            <div className="mt-2" data-testid="press3-reillustrate-progress">
              <div className="h-2 rounded-full bg-slate-100 overflow-hidden">
                <div className="h-full bg-[#E05638] transition-all"
                  style={{ width: `${reJob.total ? Math.round((reJob.done / reJob.total) * 100) : 0}%` }} />
              </div>
              <div className="mt-1 text-[11px] text-slate-500">{reJob.done || 0} / {reJob.total || 0} article(s) traité(s)…</div>
            </div>
          )}
          <button onClick={reillustrateAll} disabled={reillustrating} data-testid="press3-reillustrate-all-btn"
            className="mt-3 inline-flex items-center gap-1.5 px-4 py-2 rounded-lg bg-slate-900 text-white text-sm font-semibold hover:bg-slate-800 disabled:opacity-60">
            {reillustrating ? <Loader2 size={15} className="animate-spin" /> : <RefreshCw size={15} />}
            {reillustrating ? "Régénération en cours…" : "Régénérer les illustrations"}
          </button>
        </div>
      </div>
    </div>
  );
}
